/**
 * #2725 / #2732 / #2720 — a status change the server refused because the
 * record moved on underneath the page.
 *
 * Two tabs, two people, or one double click: the second write reaches the
 * state machine after the first, and the server answers 400/409 with the
 * model's raw `Illegal transition: withdrawn → staff_declined`. The server is
 * right; the page was stale. So on a refusal the page re-reads the record and
 * says, in words, what already happened ("The family withdrew this application
 * while you were reviewing it."). The raw transition string is never shown.
 *
 * A refusal whose re-read shows the SAME status the page was acting on is not
 * a race (a missing reason, say): `explainRefusedTransition` returns null and
 * the caller keeps its own message, scrubbed by `withoutRawTransition`.
 *
 * Staff wording, English literals by ruling (DECISIONS 2026-09-30). The family
 * side (ApplicationStatusPage) uses only the re-read: its screen already has a
 * catalog line for every status it can land on.
 */

/** The smallest record shape the helper needs back from a re-read. */
export interface StatusRecord {
  status: string;
}

/** A 400 or 409 from a state change: the server refused the move. */
export function isRefusedTransition(err: unknown): boolean {
  const code = (err as { response?: { status?: number } } | null)?.response?.status;
  return code === 400 || code === 409;
}

const RAW_TRANSITION = /illegal transition/i;

/** True when a server message is the state machine's raw refusal. */
export function isRawTransitionMessage(message: string | null | undefined): boolean {
  return !!message && RAW_TRANSITION.test(message);
}

/**
 * Said in place of a raw transition the page could not explain. True whether
 * the record moved elsewhere or the move was never allowed from its stage.
 */
export const CHANGED_ELSEWHERE =
  'That change can’t be made from where this application stands now. Reload the page to see the latest.';

/** Replace the raw `Illegal transition: a → b` with plain words; pass anything else through. */
export function withoutRawTransition(message: string): string {
  return isRawTransitionMessage(message) ? CHANGED_ELSEWHERE : message;
}

/** What already happened, by the status the record holds now. */
const ALREADY: Record<string, string> = {
  withdrawn: 'The family withdrew this application while you were reviewing it.',
  submitted: 'The family sent this application back in, so it is waiting to be opened again.',
  under_review: 'This application is back under review.',
  needs_more_info: 'Someone already asked the family for more information.',
  staff_recommended: 'This application was already moved to the committee package.',
  committee_approved: 'The committee’s approval was already recorded for this family.',
  staff_declined: 'This application was already marked not eligible or declined.',
  waitlisted: 'This family was already put on the waitlist.',
  offer_sent: 'The welcome email was already sent.',
  offer_signed: 'The family’s acceptance was already recorded.',
  offer_declined: 'The family already declined the admission letter.',
  offer_voided: 'The admission letter was already voided.',
  admitted: 'This family has already been admitted.',
};

/**
 * The sentence for a record that now holds `current`. When `current` is the
 * status the refused write was trying to reach (a double click), the move
 * simply landed already.
 */
export function refusedTransitionSentence(current: string, attempted?: string): string {
  if (attempted && current === attempted) return 'That decision was already recorded.';
  return ALREADY[current] ?? 'This application changed while you had it open.';
}

export interface RefusalExplained<T extends StatusRecord> {
  /** The record as it stands now. */
  current: T;
  /** One plain sentence: what already happened. */
  message: string;
}

/**
 * On a refused state change, re-read the record and say what already happened.
 *
 * - `err` is what the write threw. Anything but a 400/409 → null.
 * - `refetch` reads the record fresh. If it throws or comes back empty the
 *   page cannot say what moved → null, and the caller keeps its own message
 *   (passed through `withoutRawTransition`).
 * - `from` is the status the page showed when the write was sent. A re-read
 *   that still shows it means nothing moved: the refusal had another cause,
 *   so → null and the caller keeps its own message.
 * - `attempted` is the status the write was trying to reach, if known.
 */
export async function explainRefusedTransition<T extends StatusRecord>(
  err: unknown,
  refetch: () => Promise<T | null | undefined>,
  opts: { from?: string; attempted?: string } = {},
): Promise<RefusalExplained<T> | null> {
  if (!isRefusedTransition(err)) return null;
  let current: T | null;
  try {
    current = (await refetch()) ?? null;
  } catch {
    return null;
  }
  if (!current?.status) return null;
  if (opts.from && current.status === opts.from) return null;
  return { current, message: refusedTransitionSentence(current.status, opts.attempted) };
}
